var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { computed, observable } from "mobx";
import { FormModel } from "./FormModel";
import { MessageRole } from "healthex-common/aiassistant.dtos";
import DOMPurify from "dompurify";
export class AIAssistantViewModel {
    constructor(opts) {
        this.conversations = [];
        this.activeConversationId = null;
        this.protocolDocumentUrl = null;
        this.protocolDocumentName = null;
        this.messages = [];
        this.isStreaming = false;
        this.editingConversationId = null;
        this.editingTitle = "";
        this.currentPageNumber = 1;
        this.maxQueryLength = 500;
        this.formModel = new FormModel({
            query: "",
        }, ["query"]);
        this.aiAssistantManager = opts.container.aiAssistantManager;
        this.bannerUIManager = opts.container.bannerUIManager;
        void this.loadConversations();
    }
    async loadConversations() {
        try {
            const conversations = await this.aiAssistantManager.getConversations();
            this.conversations = conversations;
            if (conversations.length > 0 && !this.activeConversationId) {
                this.setActiveConversation(conversations[0].id);
            }
        }
        catch (error) {
            console.error("Failed to load conversations:", error);
        }
    }
    async createNewConversation(title) {
        try {
            const truncatedTitle = title && title.length > 25 ? `${title.substring(0, 25)}...` : title;
            const newConversation = await this.aiAssistantManager.createConversation(truncatedTitle);
            this.conversations.unshift(newConversation);
            this.setActiveConversation(newConversation.id);
            return newConversation;
        }
        catch (error) {
            console.error("Failed to create conversation:", error);
            throw error;
        }
    }
    setActiveConversation(conversationId) {
        this.activeConversationId = conversationId;
        void this.loadConversationMessages(conversationId);
    }
    async loadConversationMessages(conversationId) {
        try {
            const messages = await this.aiAssistantManager.getConversationMessages(conversationId);
            this.messages = messages;
        }
        catch (error) {
            console.error("Failed to load conversation messages:", error);
        }
    }
    get messageStreamingKeyInfo() {
        var _a;
        const lastMessage = this.messages.pop();
        if (!lastMessage) {
            return { key: "loading", isAutoScroll: false, scrollBehavior: "smooth" };
        }
        else if ((lastMessage === null || lastMessage === void 0 ? void 0 : lastMessage.role) !== MessageRole.AI) {
            return null;
        }
        else if ((_a = lastMessage.content) === null || _a === void 0 ? void 0 : _a.length) {
            return { key: `${lastMessage.id}-content-${lastMessage.content.length}`, isAutoScroll: true, scrollBehavior: "instant" };
        }
        else if (lastMessage.status === "inProgress") {
            return { key: `${lastMessage.id}-inProgress`, isAutoScroll: false, scrollBehavior: "smooth" };
        }
        else {
            return null;
        }
    }
    sanitizeUserContent(input) {
        return DOMPurify.sanitize(input, {
            ALLOWED_TAGS: ["p", "br", "ul", "ol", "li", "strong", "em", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "code", "pre"],
            KEEP_CONTENT: true,
        });
    }
    async sendQuery() {
        if (this.isStreaming) {
            console.warn("Streaming is already in progress.");
            return;
        }
        const query = this.sanitizeUserContent(this.formModel.input.query.trim());
        let displayQuery = query;
        try {
            const parsed = JSON.parse(query);
            if (parsed.tool && parsed.parameters && parsed.parameters.page) {
                displayQuery = "Show more results...";
            }
        }
        catch (e) {
            console.debug("Not a JSON query:", e);
        }
        if (!query || !this.activeConversationId) {
            console.warn("Empty query or no active conversation.");
            return;
        }
        if (query.length > this.maxQueryLength) {
            this.messages.push({
                id: crypto.randomUUID(),
                organizationId: "",
                tokenCount: 0,
                createdAt: new Date().toISOString(),
                content: `⚠️ Your message is too long. Please limit your input to ${this.maxQueryLength} characters. Current length: ${query.length} characters.`,
                status: "completed",
                chatConversationId: this.activeConversationId,
                role: MessageRole.AI,
            });
            return;
        }
        this.isStreaming = true;
        this.updateInput();
        this.messages.push({
            id: crypto.randomUUID(),
            organizationId: "",
            tokenCount: 0,
            createdAt: new Date().toISOString(),
            content: displayQuery,
            status: "completed",
            chatConversationId: this.activeConversationId,
            role: MessageRole.Human,
        });
        const aiMessage = {
            id: crypto.randomUUID(),
            organizationId: "",
            tokenCount: 0,
            createdAt: new Date().toISOString(),
            content: "",
            status: "inProgress",
            chatConversationId: this.activeConversationId,
            role: MessageRole.AI,
        };
        this.messages.push(aiMessage);
        const messageIndex = this.messages.length - 1;
        try {
            await this.aiAssistantManager.call(query, this.activeConversationId, {
                onToken: (token) => {
                    const message = this.messages[messageIndex];
                    if (!message)
                        return;
                    const metadataMatch = token.match(/\[METADATA\](.*?)\[\/METADATA\]/);
                    if (metadataMatch) {
                        try {
                            const metadata = JSON.parse(metadataMatch[1]);
                            message.metadata = {
                                ...metadata,
                                noToolsUsed: metadata.toolsUsed.length === 0,
                                footer: {
                                    text: this.aiAssistantManager.getFooterText(metadata),
                                    type: metadata.toolsUsed.length === 0 ? "warning" : "success",
                                },
                            };
                        }
                        catch (e) {
                            console.error("Failed to parse metadata:", e);
                        }
                        return;
                    }
                    if (token.includes("[DONE]"))
                        return;
                    message.content += token;
                },
                onComplete: () => {
                    const message = this.messages[messageIndex];
                    if (message) {
                        message.status = "completed";
                    }
                    this.isStreaming = false;
                },
            });
        }
        catch (error) {
            const message = this.messages[messageIndex];
            if (message) {
                message.content = `⚠️ Error: ${error instanceof Error ? error.message : "Unknown error occurred"}`;
                message.status = "completed";
            }
            this.isStreaming = false;
        }
    }
    updateInput() {
        this.formModel.input.query = "";
    }
    closeProtocolDocumentDownloadUrl() {
        this.protocolDocumentUrl = null;
        this.protocolDocumentName = null;
    }
    async getStudyProtocolDocumentDownloadUrl(s3Url, pageNumber = 1) {
        const result = await this.aiAssistantManager.getStudyProtocolDocumentDownloadUrl(s3Url, true);
        console.log("getStudyProtocolDocumentDownloadUrl", pageNumber);
        if (result) {
            this.protocolDocumentUrl = result.signedUrl;
            this.protocolDocumentName = result.fileName;
            this.currentPageNumber = pageNumber;
        }
    }
    async getICFFormDownloadUrl(s3Url, pageNumber = 1) {
        const result = await this.aiAssistantManager.getICFFormDownloadUrl(s3Url, true);
        if (result) {
            this.protocolDocumentUrl = result.signedUrl;
            this.protocolDocumentName = result.fileName;
            this.currentPageNumber = pageNumber;
        }
    }
    startEditing(conversationId, title) {
        this.editingConversationId = conversationId;
        this.editingTitle = title;
    }
    async saveConversationTitle() {
        if (this.editingConversationId) {
            await this.aiAssistantManager.renameConversation(this.editingConversationId, this.editingTitle);
            const conversation = this.conversations.find((c) => c.id === this.editingConversationId);
            if (conversation) {
                conversation.title = this.editingTitle;
            }
        }
        this.editingConversationId = null;
    }
    cancelEditing() {
        this.editingConversationId = null;
        this.editingTitle = "";
    }
    async deleteConversation(conversationId) {
        try {
            await this.aiAssistantManager.deleteConversation(conversationId);
            if (conversationId === this.activeConversationId) {
                this.activeConversationId = null;
                this.messages = [];
            }
            this.conversations = this.conversations.filter((c) => c.id !== conversationId);
        }
        catch (error) {
            console.error("Failed to delete conversation:", error);
        }
    }
    setEditingTitle(title) {
        this.editingTitle = title;
    }
    async handleLoadMore(pagination) {
        if (!pagination || !this.activeConversationId)
            return;
        const { toolName, toolParameters, currentPage } = pagination;
        const nextPage = currentPage + 1;
        this.formModel.input.query = JSON.stringify({
            tool: toolName,
            parameters: {
                ...toolParameters,
                page: nextPage,
            },
        });
        await this.sendQuery();
    }
    showCopySuccess(successMessage) {
        this.bannerUIManager.displayBanner({ text: successMessage });
    }
}
__decorate([
    observable,
    __metadata("design:type", Array)
], AIAssistantViewModel.prototype, "conversations", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AIAssistantViewModel.prototype, "activeConversationId", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AIAssistantViewModel.prototype, "protocolDocumentUrl", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AIAssistantViewModel.prototype, "protocolDocumentName", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], AIAssistantViewModel.prototype, "messages", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AIAssistantViewModel.prototype, "isStreaming", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AIAssistantViewModel.prototype, "editingConversationId", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], AIAssistantViewModel.prototype, "editingTitle", void 0);
__decorate([
    observable,
    __metadata("design:type", Number)
], AIAssistantViewModel.prototype, "currentPageNumber", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AIAssistantViewModel.prototype, "messageStreamingKeyInfo", null);
